import { AsyncSelect, type IAsyncSelectProps } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { accountRepository } from '@webapp/platform/finance/data-access/repositories/AccountRepository'
import { Account } from '@webapp/platform/finance/entities/Account'
import messages from './messages'

export interface IAccountSelectProps extends IAsyncSelectProps<Account> {
  /** Scope requestable account options to a SecondaryCategory (department) */
  secondaryCategoryId?: number
}

/**
 * Self-contained account option select (ADR-3 shared component, not a feature).
 * Consumed by list account cells and account-code correction forms across
 * business modules.
 */
export const AccountSelect = ({
  secondaryCategoryId,
  ...props
}: IAccountSelectProps) => {
  const intl = useProcurifyIntl()

  const loadOptions: IAsyncSelectProps<Account>['loadOptions'] = async (
    inputValue,
    pagination,
    callback
  ) => {
    const res = await accountRepository.getRequestableAccounts(
      secondaryCategoryId,
      {
        active: true,
        search: inputValue,
        page: pagination.page,
      }
    )

    callback({
      options: res.data.map((account) => new Account(account)),
      pagination: {
        hasMore: res.pagination
          ? res.pagination.page < res.pagination.numPages
          : false,
        page: pagination.page + 1,
      },
    })
  }

  return (
    <AsyncSelect<Account>
      // Options are scoped by secondaryCategoryId — bust the cache on change
      key={secondaryCategoryId}
      cacheOptions={true}
      loadOptions={loadOptions}
      placeholder={intl.formatMessage(messages.placeholderText)}
      getOptionLabel={(account) => account.accountCodeLabel}
      getOptionValue={(option) => (option ? option.id.toString() : '')}
      {...props}
    />
  )
}
